import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { api, setToken, getToken, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, ArrowLeft } from "lucide-react";
import { RESTAURANT } from "@/lib/content";
import { useSiteContent, DEFAULT_CONTENT } from "@/lib/site-content";

import AdminGallery from "@/components/admin/AdminGallery";
import AdminMenu from "@/components/admin/AdminMenu";
import AdminReviews from "@/components/admin/AdminReviews";
import AdminHero from "@/components/admin/AdminHero";
import AdminAbout from "@/components/admin/AdminAbout";
import AdminHours from "@/components/admin/AdminHours";
import AdminContactInfo from "@/components/admin/AdminContactInfo";
import AdminReservations from "@/components/admin/AdminReservations";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => {
        setToken(null);
        setAuthed(false);
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF8F5]">
        <Loader2 className="h-8 w-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-page">
      <header className="border-b border-[#E8DED5] bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={RESTAURANT.logo}
              alt="logo"
              className="h-10 w-10 rounded-lg object-cover border border-[#E8DED5]"
            />
            <div>
              <div className="font-display font-black text-[15px] tracking-tight">
                Thai Noodle & Rice
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#5C534D]">
                Admin · Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              data-testid="admin-back-to-site"
              onClick={() => navigate("/")}
              className="h-10 rounded-full text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to site
            </Button>
            {authed && (
              <Button
                data-testid="admin-logout"
                onClick={() => {
                  setToken(null);
                  setAuthed(false);
                  toast.success("Logged out");
                }}
                variant="outline"
                className="h-10 rounded-full text-sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 md:py-14">
        {!authed ? <LoginForm onSuccess={() => setAuthed(true)} /> : <Dashboard />}
      </main>
    </div>
  );
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success("Welcome back!");
      onSuccess();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10">
      <Card className="rounded-3xl border-[#E8DED5] shadow-[0_20px_60px_-30px_rgba(26,26,26,0.25)]">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#E53935]/10 grid place-items-center text-[#E53935] mb-2">
            <Lock className="h-5 w-5" />
          </div>
          <CardTitle className="font-display text-2xl">Admin Sign-in</CardTitle>
          <CardDescription>
            Enter your credentials to manage the site.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} data-testid="admin-login-form" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl"
                placeholder="admin@thainoodlerice.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 rounded-xl"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#E53935] hover:bg-[#C62828] text-white font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Dashboard() {
  const { content, refresh, loading } = useSiteContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      setDraft(JSON.parse(JSON.stringify(content)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!draft) {
    return (
      <div className="grid place-items-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-[#E53935]" />
      </div>
    );
  }

  const update = (section, next) =>
    setDraft((d) => ({ ...d, [section]: next }));

  const saveSection = async (section, value) => {
    const nextDraft = { ...draft, [section]: value };
    setDraft(nextDraft);
    setSaving(true);
    try {
      await api.put("/admin/content", nextDraft);
      toast.success("Saved — live on the site.");
      refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (section) => {
    const defaults = DEFAULT_CONTENT[section];
    saveSection(section, JSON.parse(JSON.stringify(defaults)));
  };

  return (
    <Tabs defaultValue="gallery" className="w-full">
      <TabsList
        data-testid="admin-tabs"
        className="w-full flex flex-wrap h-auto bg-white border border-[#E8DED5] rounded-full p-1.5 gap-1 mb-10"
      >
        {[
          ["gallery", "Gallery"],
          ["menu", "Menu"],
          ["reviews", "Reviews"],
          ["hero", "Hero"],
          ["about", "About"],
          ["hours", "Hours"],
          ["contact", "Contact"],
          ["reservations", "Reservations"],
        ].map(([v, l]) => (
          <TabsTrigger
            key={v}
            value={v}
            data-testid={`admin-tab-${v}`}
            className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#E53935] data-[state=active]:text-white"
          >
            {l}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="gallery">
        <AdminGallery />
      </TabsContent>

      <TabsContent value="menu">
        <AdminMenu
          value={draft.menu}
          onChange={(v) => update("menu", v)}
          onSave={(v) => saveSection("menu", v)}
          fullMenuImage={draft.full_menu_image}
          onFullMenuImageSave={(url) => saveSection("full_menu_image", url)}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="reviews">
        <AdminReviews
          value={draft.reviews}
          onChange={(v) => update("reviews", v)}
          onSave={(v) => saveSection("reviews", v)}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="hero">
        <AdminHero
          value={draft.hero}
          onChange={(v) => update("hero", v)}
          onSave={(v) => saveSection("hero", v)}
          onReset={() => resetSection("hero")}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="about">
        <AdminAbout
          value={draft.about}
          onChange={(v) => update("about", v)}
          onSave={(v) => saveSection("about", v)}
          onReset={() => resetSection("about")}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="hours">
        <AdminHours
          value={draft.hours}
          onChange={(v) => update("hours", v)}
          onSave={(v) => saveSection("hours", v)}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="contact">
        <AdminContactInfo
          value={draft.contact}
          onChange={(v) => update("contact", v)}
          onSave={(v) => saveSection("contact", v)}
          saving={saving}
        />
      </TabsContent>

      <TabsContent value="reservations">
        <AdminReservations />
      </TabsContent>
    </Tabs>
  );
}
